import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiGet } from '@/api/client';
import type { PlatformUser } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Input, Select } from '@/app/components/ui/Field';
import { Modal } from '@/app/components/ui/Modal';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { formatDate } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';
import { AdminSubmitError } from '@/manage/subscriptions/errors';
import {
  cancelUserMollieSubscription,
  getUserLedger,
  grantAccess,
  movePurchase,
  recomputeEntitlement,
  retryMollieSubscription,
  revokePurchase,
  type SubscriptionAdminLedger,
} from '@/manage/subscriptions/api';

const PERIOD_UNITS = ['day', 'month', 'year'] as const;
const SEARCH_RESULT_LIMIT = 8;

/**
 * "Beheer › Abonnementen › Gebruikersoverzicht" (§ 2): look up a user's
 * ledger and act on it. No optimistic updates — every mutation reloads the
 * ledger afterwards, same pattern as `SupportTicketDetailPage`.
 *
 * There is no user-search endpoint of its own; this reuses `/users/all`
 * (the same listing People › All users renders) so an admin can find someone
 * by name/email instead of having to already know their raw user id.
 */
export function UserLedgerPage() {
  const { t } = useTranslation();
  const [userId, setUserId] = useState<string | null>(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [userIdInput, setUserIdInput] = useState('');

  const users = useAsync(() => apiGet('/users/all') as unknown as Promise<PlatformUser[]>, []);
  const ledger = useAsync(() => (userId ? getUserLedger(userId) : Promise.resolve(null)), [userId]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('manageSubscriptions.userLedger.title')}
        lead={t('manageSubscriptions.userLedger.lead')}
      />

      {manualEntry ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setUserId(userIdInput.trim() || null);
          }}
        >
          <Field label={t('manageSubscriptions.userLedger.lookupLabel')} className="w-96">
            <Input value={userIdInput} onChange={(e) => setUserIdInput(e.target.value)} required />
          </Field>
          <Button type="submit">{t('manageSubscriptions.userLedger.lookupSubmit')}</Button>
          <button
            type="button"
            className="text-accent text-[13px] underline"
            onClick={() => setManualEntry(false)}
          >
            {t('manageSubscriptions.userLedger.searchInstead')}
          </button>
        </form>
      ) : (
        <div className="flex flex-col items-start gap-2">
          <UserPicker
            label={t('manageSubscriptions.userLedger.searchLabel')}
            users={users.data ?? undefined}
            onSelect={(user) => setUserId(user.user_id)}
          />
          <button
            type="button"
            className="text-accent text-[13px] underline"
            onClick={() => setManualEntry(true)}
          >
            {t('manageSubscriptions.userLedger.manualEntryInstead')}
          </button>
        </div>
      )}

      {userId && (
        <AsyncSection state={ledger} label={t('manageSubscriptions.userLedger.title')}>
          {(data) =>
            data ? <LedgerView userId={userId} ledger={data} onDone={ledger.reload} /> : null
          }
        </AsyncSection>
      )}
    </section>
  );
}

/** Name/email typeahead over an already-fetched user list — filters
 * client-side since `/users/all` has no search query param of its own. */
function UserPicker({
  label,
  users,
  onSelect,
}: {
  label: string;
  users: PlatformUser[] | undefined;
  onSelect: (user: PlatformUser) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2 || !users) return [];
    return users
      .filter((user) =>
        [user.name, user.nickname, user.email].some((field) =>
          field?.toLowerCase().includes(needle),
        ),
      )
      .slice(0, SEARCH_RESULT_LIMIT);
  }, [query, users]);

  return (
    <div className="w-96">
      <Field label={label}>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('manageSubscriptions.userLedger.searchPlaceholder')}
        />
      </Field>
      {query.trim().length >= 2 && (
        <ul className="border-line mt-2 flex flex-col gap-1 rounded-xl border p-1">
          {matches.length === 0 ? (
            <li className="text-accent px-2 py-1.5 text-[13px]">
              {t('manageSubscriptions.userLedger.searchNoResults')}
            </li>
          ) : (
            matches.map((user) => (
              <li key={user.user_id}>
                <button
                  type="button"
                  className="hover:bg-mint-tint w-full rounded-lg px-2 py-1.5 text-left"
                  onClick={() => {
                    setQuery('');
                    onSelect(user);
                  }}
                >
                  <p className="text-ink text-sm">{user.name ?? user.nickname ?? user.email}</p>
                  {user.email && <p className="text-accent text-[13px]">{user.email}</p>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function LedgerView({
  userId,
  ledger,
  onDone,
}: {
  userId: string;
  ledger: SubscriptionAdminLedger;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const entitlement = ledger.entitlement;
  const purchases = ledger.purchases ?? [];
  const mollie = ledger.mollie_subscription ?? null;

  const recompute = useSubmit(() => recomputeEntitlement(userId).then(() => onDone()));
  const cancelMollie = useSubmit(() => cancelUserMollieSubscription(userId).then(() => onDone()));
  const sync = useSubmit((mollieSubscriptionId: string) =>
    retryMollieSubscription(mollieSubscriptionId).then(() => onDone()),
  );
  const revoke = useSubmit((purchaseId: string) => revokePurchase(purchaseId).then(() => onDone()));
  const [moving, setMoving] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader title={t('manageSubscriptions.userLedger.entitlementTitle')} />
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone={entitlement?.active ? 'mint' : 'outline'}>
            {entitlement?.active
              ? t('manageSubscriptions.userLedger.active')
              : t('manageSubscriptions.userLedger.inactive')}
          </Badge>
          {entitlement?.tier && <span className="text-ink text-sm">{entitlement.tier}</span>}
          {entitlement?.expires_at && (
            <span className="text-ink text-sm">
              {t('manageSubscriptions.userLedger.expiresAt')}: {formatDate(entitlement.expires_at)}
            </span>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={recompute.pending}
            onClick={() => void recompute.submit()}
          >
            {t('manageSubscriptions.userLedger.recompute')}
          </Button>
        </div>
        <AdminSubmitError error={recompute.error} />
      </Card>

      <GrantCard userId={userId} onDone={onDone} />

      <Card>
        <CardHeader title={t('manageSubscriptions.userLedger.mollieTitle')} />
        {mollie ? (
          <div className="flex flex-col gap-3">
            <p className="text-ink text-sm">
              {t('manageSubscriptions.userLedger.mollieStatus')}: {mollie.status ?? '—'}
            </p>
            <p className="text-ink text-sm">
              {t('manageSubscriptions.userLedger.mollieFailedPayments')}:{' '}
              {mollie.failed_payment_count ?? 0}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={cancelMollie.pending}
                onClick={() => void cancelMollie.submit()}
              >
                {t('manageSubscriptions.userLedger.cancelMollie')}
              </Button>
              {mollie.id && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={sync.pending}
                  onClick={() => void sync.submit(mollie.id!)}
                >
                  {t('manageSubscriptions.userLedger.syncStatus')}
                </Button>
              )}
            </div>
            <AdminSubmitError error={cancelMollie.error} />
            <AdminSubmitError error={sync.error} />
          </div>
        ) : (
          <EmptyState text={t('manageSubscriptions.userLedger.mollieNone')} />
        )}
      </Card>

      <Card padding="none">
        <div className="p-5 pb-0">
          <CardHeader title={t('manageSubscriptions.userLedger.purchasesTitle')} />
        </div>
        {purchases.length === 0 ? (
          <div className="p-5 pt-0">
            <EmptyState text={t('manageSubscriptions.userLedger.purchasesEmpty')} />
          </div>
        ) : (
          <DataTable
            rowKey={(row) => row.purchase_id ?? ''}
            rows={purchases}
            columns={[
              {
                key: 'date',
                header: t('manageSubscriptions.userLedger.date'),
                render: (row) => (row.created_at ? formatDate(row.created_at) : '—'),
              },
              {
                key: 'period',
                header: t('manageSubscriptions.userLedger.period'),
                render: (row) =>
                  row.granted_from && row.granted_until
                    ? `${formatDate(row.granted_from)} – ${formatDate(row.granted_until)}`
                    : '—',
              },
              {
                key: 'channel',
                header: t('manageSubscriptions.userLedger.channel'),
                render: (row) => row.provider,
              },
              {
                key: 'state',
                header: t('manageSubscriptions.userLedger.state'),
                render: (row) => row.state ?? '—',
              },
              {
                key: 'actions',
                header: t('manageSubscriptions.userLedger.actions'),
                align: 'end',
                render: (row) => (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={revoke.pending}
                      onClick={() => row.purchase_id && void revoke.submit(row.purchase_id)}
                    >
                      {t('manageSubscriptions.userLedger.revoke')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => row.purchase_id && setMoving(row.purchase_id)}
                    >
                      {t('manageSubscriptions.userLedger.move')}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        )}
        <AdminSubmitError error={revoke.error} />
      </Card>

      <MovePurchaseModal
        purchaseId={moving}
        onClose={() => setMoving(null)}
        onDone={() => {
          setMoving(null);
          onDone();
        }}
      />
    </div>
  );
}

function GrantCard({ userId, onDone }: { userId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [tier, setTier] = useState('');
  const [periodAmount, setPeriodAmount] = useState('1');
  const [periodUnit, setPeriodUnit] = useState<(typeof PERIOD_UNITS)[number]>('month');
  const [reason, setReason] = useState('');

  const grant = useSubmit(async () => {
    await grantAccess(userId, {
      tier,
      period_amount: Number(periodAmount),
      period_unit: periodUnit,
      reason: reason === '' ? null : reason,
    });
    setReason('');
    onDone();
  });

  return (
    <Card>
      <CardHeader title={t('manageSubscriptions.userLedger.grantTitle')} />
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void grant.submit();
        }}
      >
        <Field label={t('manageSubscriptions.userLedger.grantFieldTier')} className="w-40">
          <Input value={tier} onChange={(e) => setTier(e.target.value)} required />
        </Field>
        <Field label={t('manageSubscriptions.userLedger.grantFieldAmount')} className="w-32">
          <Input
            type="number"
            min={1}
            value={periodAmount}
            onChange={(e) => setPeriodAmount(e.target.value)}
            required
          />
        </Field>
        <Field label={t('manageSubscriptions.userLedger.grantFieldUnit')} className="w-40">
          <Select
            value={periodUnit}
            onChange={(e) => setPeriodUnit(e.target.value as (typeof PERIOD_UNITS)[number])}
          >
            {PERIOD_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {t(`manageSubscriptions.products.periodUnit_${unit}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('manageSubscriptions.userLedger.grantFieldReason')} className="w-64">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Button type="submit" disabled={grant.pending}>
          {t('manageSubscriptions.userLedger.grantSubmit')}
        </Button>
      </form>
      <AdminSubmitError error={grant.error} />
    </Card>
  );
}

function MovePurchaseModal({
  purchaseId,
  onClose,
  onDone,
}: {
  purchaseId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [toUserId, setToUserId] = useState('');
  const [toUserLabel, setToUserLabel] = useState('');
  const users = useAsync(
    () =>
      purchaseId
        ? (apiGet('/users/all') as unknown as Promise<PlatformUser[]>)
        : Promise.resolve(undefined),
    [purchaseId],
  );

  const move = useSubmit(async () => {
    if (!purchaseId) return;
    await movePurchase(purchaseId, toUserId);
    setToUserId('');
    setToUserLabel('');
    onDone();
  });

  return (
    <Modal
      open={purchaseId !== null}
      onClose={onClose}
      title={t('manageSubscriptions.userLedger.move')}
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void move.submit();
        }}
      >
        <UserPicker
          label={t('manageSubscriptions.userLedger.searchLabel')}
          users={users.data ?? undefined}
          onSelect={(user) => {
            setToUserId(user.user_id);
            setToUserLabel(user.name ?? user.nickname ?? user.email ?? user.user_id);
          }}
        />
        <Field
          label={t('manageSubscriptions.userLedger.moveFieldToUserId')}
          hint={
            toUserLabel
              ? t('manageSubscriptions.userLedger.moveSelectedHint', { name: toUserLabel })
              : undefined
          }
        >
          <Input
            value={toUserId}
            onChange={(e) => {
              setToUserId(e.target.value);
              setToUserLabel('');
            }}
            required
          />
        </Field>
        <div className="flex justify-end gap-3">
          <AdminSubmitError error={move.error} />
          <Button type="submit" disabled={move.pending}>
            {t('manageSubscriptions.userLedger.moveSubmit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
